/**
 * Native Audio Recording Utility
 * 
 * Provides functionality to record audio using the Natively Audio Recorder API
 * for use in native iOS/Android apps.
 */

import { toast } from "sonner";

export interface AudioRecordingResponse {
  base64: string;
  content_type: string; // "audio/m4a" for ios or "audio/wav" for android
  size: number; // file size in KB
  status: 'SUCCESS' | 'CANCELLED';
}

export interface AudioRecorderResult {
  success: boolean;
  data?: AudioRecordingResponse;
  error?: string;
}

/**
 * Check if audio recording is available (native app or browser)
 */
export const isAudioRecordingAvailable = (): boolean => {
  if (typeof window === 'undefined') {
    return false;
  }
  
  // Check for native audio recorder
  const hasNativeRecorder = !!window.NativelyAudioRecorder;
  
  // Check for browser MediaRecorder API
  const hasBrowserRecorder = !!(navigator.mediaDevices && navigator.mediaDevices.getUserMedia && window.MediaRecorder);
  
  return hasNativeRecorder || hasBrowserRecorder;
};

/**
 * Record audio using the browser MediaRecorder API
 * Maximum recording time: 15 seconds
 */
const recordAudioBrowser = async (): Promise<AudioRecorderResult> => {
  return new Promise(async (resolve) => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream);
      const audioChunks: Blob[] = [];
      let recordingTimeout: number;
      
      mediaRecorder.ondataavailable = (event) => {
        audioChunks.push(event.data);
      };
      
      mediaRecorder.onstop = async () => {
        // Stop all tracks to release microphone
        stream.getTracks().forEach(track => track.stop());
        clearTimeout(recordingTimeout);
        
        const audioBlob = new Blob(audioChunks, { type: 'audio/webm' });
        const sizeKB = audioBlob.size / 1024;
        
        // Convert to base64
        const reader = new FileReader();
        reader.onloadend = () => {
          const base64 = (reader.result as string).split(',')[1];
          resolve({
            success: true,
            data: {
              base64,
              content_type: 'audio/webm',
              size: sizeKB,
              status: 'SUCCESS',
            },
          });
        };
        reader.readAsDataURL(audioBlob);
      };
      
      mediaRecorder.onerror = () => {
        stream.getTracks().forEach(track => track.stop());
        clearTimeout(recordingTimeout);
        resolve({
          success: false,
          error: 'Recording error occurred',
        });
      };
      
      // Start recording
      mediaRecorder.start();
      toast.info("Recording... (max 15 seconds)", { duration: 3000 });
      
      // Stop after 15 seconds
      recordingTimeout = setTimeout(() => {
        if (mediaRecorder.state !== 'inactive') {
          mediaRecorder.stop();
        }
      }, 15000);
      
      // Listen for user stopping early (we'll need to expose a stop method)
      // For now, it will auto-stop after 15 seconds
      
    } catch (error) {
      if (error instanceof DOMException && error.name === 'NotAllowedError') {
        resolve({
          success: false,
          error: 'Microphone access denied. Please allow microphone access in your browser settings.',
        });
      } else {
        resolve({
          success: false,
          error: error instanceof Error ? error.message : 'Unknown error during recording',
        });
      }
    }
  });
};

/**
 * Record audio using the native audio recorder (mobile app)
 * Maximum recording time: 15 seconds (enforced by native SDK)
 */
const recordAudioNative = async (): Promise<AudioRecorderResult> => {
  return new Promise((resolve) => {
    try {
      const recorder = new window.NativelyAudioRecorder();
      
      // Set a timeout in case the callback never fires
      const timeout = setTimeout(() => {
        resolve({
          success: false,
          error: 'Recording timeout - no response from native recorder',
        });
      }, 20000);

      const show_recorder_callback = function (resp: any) {
        clearTimeout(timeout);
        
        if (resp.status === 'CANCELLED') {
          resolve({
            success: false,
            error: 'Recording cancelled by user',
          });
          return;
        }
        
        if (!resp.base64) {
          resolve({
            success: false,
            error: 'No audio data received from recorder',
          });
          return;
        }
        
        resolve({
          success: true,
          data: resp as AudioRecordingResponse,
        });
      };
      
      // Call with max_duration (15 seconds) and callback
      // showRecorder(max_duration_seconds, callback)
      recorder.showRecorder(15, show_recorder_callback);
    } catch (error) {
      resolve({
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error during recording',
      });
    }
  });
};

/**
 * Record audio using either native recorder (mobile app) or browser API (desktop/mobile web)
 * Maximum recording time: 15 seconds
 * 
 * @returns Promise<AudioRecorderResult> - Recording result with audio data or error
 */
export const recordAudio = async (): Promise<AudioRecorderResult> => {
  if (!isAudioRecordingAvailable()) {
    toast.error('Audio recording not available. Please check your browser permissions.', { duration: 5000 });
    return {
      success: false,
      error: 'Audio recording is not available.',
    };
  }

  // Use native recorder if available, otherwise use browser API
  if (window.NativelyAudioRecorder) {
    return recordAudioNative();
  } else {
    return recordAudioBrowser();
  }
};

/**
 * Convert audio size from KB to a human-readable string
 */
export const formatAudioSize = (sizeKB: number): string => {
  if (sizeKB < 1024) {
    return `${sizeKB.toFixed(1)} KB`;
  }
  return `${(sizeKB / 1024).toFixed(1)} MB`;
};

/**
 * Estimate audio duration from file size (rough approximation)
 * Assumes ~8-16 KB per second for compressed audio
 */
export const estimateAudioDuration = (sizeKB: number): number => {
  // Use average of 12 KB/s for estimation
  return Math.round(sizeKB / 12);
};
